'use client';

import type { SxProps, Theme } from '@mui/material/styles';

import { useEffect, useMemo, useState } from 'react';

import SolarCrownLineBold from '@/components-v2/icons/SolarCrownLineBold';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import styled from '@mui/material/styles/styled';
import useTheme from '@mui/material/styles/useTheme';

// ----------------------------------------------------------------------

// 样式化的 Slider 容器
export const SliderContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  padding: theme.spacing(1, 1.25, 0.25, 1.25),
  backgroundColor: 'transparent',
  borderRadius: theme.spacing(1.5),
  border: 'none',
  transition: 'all 0.2s ease-in-out',
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

// 样式化的 Slider 标题
export const SliderTitle = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle2" sx={{ fontSize: '0.85rem', lineHeight: 1 }}>
    {children}
  </Typography>
);

// 定义默认样式
const defaultSliderStyles: SxProps<Theme> = {
  '& .MuiSlider-thumb': {
    width: 16,
    height: 16,
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    background: '#ffffff',
    border: (theme: Theme) => `2px solid ${theme.palette.primary.main}`,
    transition: 'all 0.2s ease-in-out',
    '@media (hover: hover)': {
      '&:hover, &.Mui-focusVisible': {
        boxShadow: (theme: Theme) => `0 3px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
        transform: 'translate(-50%, -50%) scale(1.1)',
      },
    },
    '&.Mui-active': {
      boxShadow: (theme: Theme) => `0 0 0 8px ${alpha(theme.palette.primary.main, 0.16)}`,
      transform: 'translate(-50%, -50%) scale(1.2)',
    },
  },
  '& .MuiSlider-track': {
    transition: 'all 0.2s ease-in-out',
    opacity: 0.8,
  },
  '& .MuiSlider-rail': {
    opacity: 0.3,
    transition: 'all 0.2s ease-in-out',
  },
  '& .MuiSlider-mark': {
    width: 4,
    height: 4,
    borderRadius: '50%',
    opacity: 0.3,
    backgroundColor: 'currentColor',
  },
  '& .MuiSlider-markActive': {
    opacity: 0.7,
    backgroundColor: 'currentColor',
  },
  '& .MuiSlider-valueLabel': {
    fontSize: '0.75rem',
    padding: '0.2rem 0.4rem',
    lineHeight: 1,
    '&:before': {
      height: 6,
      width: 6,
      bottom: -2,
    },
  },
};

type SliderProps = React.ComponentProps<typeof Slider>;

export type CustomSliderProps = Omit<SliderProps, 'value' | 'onChange'> & {
  title?: string;
  showMarks?: boolean;
  alwaysShowLabel?: boolean;
  useContainer?: boolean;
  containerSx?: SxProps<Theme>;
  value?: number | number[];
  onChange?: (value: number | number[]) => void;
  valueLabelFormat?: (value: number) => string;
  restrictionLimit?: number;
  showRestrictedArea?: boolean;
  createRestrictedOnChange?: (
    originalOnChange: (value: number | number[]) => void,
  ) => (value: number | number[]) => void;
  triggerVisualFeedback?: boolean;
  onVisualFeedbackComplete?: () => void;
};

export function CustomSlider({
  title,
  showMarks = false,
  alwaysShowLabel = false,
  useContainer = false,
  containerSx,
  sx,
  onChange,
  value,
  valueLabelFormat,
  restrictionLimit,
  showRestrictedArea = false,
  createRestrictedOnChange,
  triggerVisualFeedback = false,
  onVisualFeedbackComplete,
  ...other
}: CustomSliderProps) {
  const theme = useTheme();
  const [isShaking, setIsShaking] = useState(false);

  // 处理视觉反馈触发
  useEffect(() => {
    if (triggerVisualFeedback) {
      setIsShaking(true);

      // 获取crown icon位置并派发事件
      const crownElement = document.getElementById('slider-crown-icon');
      const rect = crownElement?.getBoundingClientRect();

      window.dispatchEvent(
        new CustomEvent('triggerSubscribeAnimation', {
          detail: {
            startPosition: rect ? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 } : null,
            errorMessage: 'api.user_image_count_too_many',
          },
        }),
      );

      const timer = setTimeout(() => {
        setIsShaking(false);
        onVisualFeedbackComplete?.();
      }, 600);

      return () => clearTimeout(timer);
    } else {
      setIsShaking(false);
    }
  }, [triggerVisualFeedback, onVisualFeedbackComplete]);

  // 优化：使用 useMemo 缓存样式对象，包含受限区域样式和震动效果
  const sliderSx = useMemo(() => {
    const baseStyles = { ...defaultSliderStyles, ...(sx || {}) };

    let enhancedStyles = baseStyles;

    // 添加受限区域样式 - 只有当限制值小于滑块最大值时才应用
    if (showRestrictedArea && restrictionLimit && other.max && restrictionLimit < other.max) {
      const restrictedRatio = (restrictionLimit - 1) / (other.max - 1);
      const primaryColor = theme.palette.primary.main; // 左侧紫色
      const secondaryColor = theme.palette.secondary.main; // 右侧橙色

      enhancedStyles = {
        ...enhancedStyles,
        '& .MuiSlider-rail': {
          background: `linear-gradient(to right,
            ${primaryColor} 0%,
            ${primaryColor} ${restrictedRatio * 100}%,
            ${secondaryColor} ${restrictedRatio * 100}%,
            ${secondaryColor} 100%)`,
          position: 'relative',
          opacity: 0.5,
          '&::after': {
            content: '""',
            position: 'absolute',
            left: `${restrictedRatio * 100}%`,
            top: '20%',
            transform: 'translate(-50%, -50%)',
            width: '3px',
            height: '28px',
            background: `linear-gradient(to bottom, ${primaryColor}, ${secondaryColor})`,
            borderRadius: '2px',
            boxShadow: `0 0 8px ${alpha(theme.palette.common.black, 0.3)}`,
            opacity: 1,
          },
        },
        '& .MuiSlider-mark': {
          opacity: 1,
        },
      };
    }

    // 添加震动效果
    if (isShaking) {
      const primaryGlow = alpha(theme.palette.primary.main, 0.3);
      const secondaryGlow = alpha(theme.palette.secondary.main, 0.3);

      enhancedStyles = {
        ...enhancedStyles,
        animation: 'shake 0.6s ease-in-out',
        '& .MuiSlider-thumb': {
          boxShadow: `0 0 0 8px ${alpha(theme.palette.error.main, 0.3)}`,
          backgroundColor: theme.palette.error.main,
        },
        '& .MuiSlider-rail': {
          ...(enhancedStyles as any)['& .MuiSlider-rail'],
          boxShadow: `0 0 8px ${primaryGlow}, 0 0 16px ${secondaryGlow}`,
          filter: 'brightness(1.1)',
          transition: 'all 0.6s ease-in-out',
        },
        '@keyframes shake': {
          '0%, 100%': { transform: 'translateX(0)' },
          '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-4px)' },
          '20%, 40%, 60%, 80%': { transform: 'translateX(4px)' },
        },
      };
    }

    return enhancedStyles;
  }, [sx, showRestrictedArea, restrictionLimit, other.max, isShaking, theme]);

  // 计算显示值，确保值是有效的数字
  const normalizedValue = useMemo(() => {
    if (value === undefined || value === null) return other.min || 0;
    if (typeof value === 'string') {
      const numValue = Number(value);
      return isNaN(numValue) ? other.min || 0 : numValue;
    }
    return value;
  }, [value, other.min]);

  // 优化：缓存当前值，避免重复的数组检查
  const currentValue = useMemo(() => {
    return Array.isArray(normalizedValue) ? normalizedValue[0] : normalizedValue;
  }, [normalizedValue]);

  // 计算显示值用于文本展示
  const displayValue = useMemo(() => {
    return valueLabelFormat ? valueLabelFormat(currentValue) : currentValue.toString();
  }, [currentValue, valueLabelFormat]);

  // 优化：使用 useMemo 缓存渲染元素
  const sliderElement = useMemo(() => {
    if (useContainer) {
      // 容器版本的 slider（带标题和数值文本显示）
      return (
        <Stack spacing={0.25}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {title && <SliderTitle>{title}</SliderTitle>}
            <Typography
              variant="subtitle2"
              sx={{
                fontSize: '0.85rem',
                lineHeight: 1,
                minWidth: '40px',
                textAlign: 'right',
              }}
            >
              {displayValue}
            </Typography>
          </Stack>
          <Box sx={{ position: 'relative' }}>
            <Slider
              {...other}
              value={normalizedValue}
              marks={showMarks ? other.marks || true : other.marks}
              valueLabelDisplay="off"
              onChange={
                createRestrictedOnChange
                  ? (_event: Event, newValue: number | number[]) => {
                      const restrictedOnChange = createRestrictedOnChange((value: number | number[]) => {
                        onChange?.(value);
                      });
                      restrictedOnChange(newValue);
                    }
                  : (_event: Event, newValue: number | number[]) => {
                      onChange?.(newValue);
                    }
              }
              sx={sliderSx}
            />
            {showRestrictedArea && restrictionLimit && other.max && restrictionLimit < other.max && (
              <Box
                id="slider-crown-icon"
                component={SolarCrownLineBold}
                sx={{
                  position: 'absolute',
                  left: `calc(${((restrictionLimit - 1) / (other.max - 1)) * 100}% + 8px)`,
                  top: '-12px',
                  width: 16,
                  height: 16,
                  color: theme.palette.secondary.main,
                  zIndex: 3,
                }}
              />
            )}
          </Box>
        </Stack>
      );
    }

    // 普通版本的 slider（保持原有行为）
    return (
      <Stack spacing={0.25}>
        {title && <SliderTitle>{title}</SliderTitle>}
        <Slider
          {...other}
          value={normalizedValue}
          marks={showMarks ? other.marks || true : other.marks}
          valueLabelDisplay={alwaysShowLabel ? 'on' : other.valueLabelDisplay || 'auto'}
          valueLabelFormat={valueLabelFormat}
          onChange={
            createRestrictedOnChange
              ? (_event: Event, newValue: number | number[]) => {
                  const restrictedOnChange = createRestrictedOnChange((value: number | number[]) => {
                    onChange?.(value);
                  });
                  restrictedOnChange(newValue);
                }
              : (_event: Event, newValue: number | number[]) => {
                  onChange?.(newValue);
                }
          }
          sx={sliderSx}
        />
      </Stack>
    );
  }, [
    useContainer,
    title,
    displayValue,
    other,
    normalizedValue,
    showMarks,
    onChange,
    sliderSx,
    alwaysShowLabel,
    valueLabelFormat,
  ]);

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
  };

  return useContainer || containerSx ? (
    <SliderContainer sx={containerSx} onTouchStart={handleTouchStart}>
      {sliderElement}
    </SliderContainer>
  ) : (
    <div onTouchStart={handleTouchStart}>{sliderElement}</div>
  );
}
